import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Plus, Code2, Trash2, ExternalLink, Clock, Zap, Settings, Users,
  Save, X, Search, Globe, FolderOpen, ChevronRight, MoreHorizontal,
  FileText, Layers, BookOpen, Shield, Bell, LogOut
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import ThemeToggle from '../components/ThemeToggle';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces]   = useState([]);
  const [activeWs,   setActiveWs]     = useState(null);
  const [projects,   setProjects]     = useState([]);
  const [members,    setMembers]      = useState([]);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showCreateWs,      setShowCreateWs]      = useState(false);
  const [showWsSettings,    setShowWsSettings]    = useState(false);
  const [projectForm, setProjectForm] = useState({ name:'', description:'', base_url:'' });
  const [wsForm,      setWsForm]      = useState({ name:'', description:'' });
  const [loading,     setLoading]     = useState(false);
  const [searchQ,     setSearchQ]     = useState('');
  const [notifCount,  setNotifCount]  = useState(0);

  useEffect(() => { loadWorkspaces(); loadNotifs(); }, []);
  useEffect(() => { if (activeWs) { loadProjects(activeWs.id); loadMembers(activeWs.id); } }, [activeWs]);

  async function loadWorkspaces() {
    try {
      if (user?.role === 'client') {
        const r = await api.get('/my-assigned-projects');
        setProjects(r.data);
        return;
      }
      const r = await api.get('/workspaces');
      setWorkspaces(r.data);
      if (r.data.length) setActiveWs(r.data[0]);
    } catch {}
  }
  async function loadProjects(id)  { try { const r = await api.get(`/workspaces/${id}/projects`); setProjects(r.data); } catch {} }
  async function loadMembers(id)   { try { const r = await api.get(`/workspaces/${id}/members`);  setMembers(r.data);  } catch {} }
  async function loadNotifs()      { try { const r = await api.get('/notifications');              setNotifCount(r.data.unread||0); } catch {} }

  async function createProject(e) {
    e.preventDefault(); setLoading(true);
    try {
      const r = await api.post(`/workspaces/${activeWs.id}/projects`, projectForm);
      toast.success('Project created!');
      setShowCreateProject(false);
      setProjectForm({ name:'', description:'', base_url:'' });
      loadProjects(activeWs.id);
      navigate(`/project/${r.data.id}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  }

  async function createWorkspace(e) {
    e.preventDefault(); setLoading(true);
    try {
      await api.post('/workspaces', wsForm);
      toast.success('Workspace created!');
      setShowCreateWs(false);
      setWsForm({ name:'', description:'' });
      loadWorkspaces();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  }

  async function updateWorkspace(e) {
    e.preventDefault();
    try {
      await api.put(`/workspaces/${activeWs.id}`, wsForm);
      setActiveWs(w => ({ ...w, ...wsForm }));
      setWorkspaces(ws => ws.map(w => w.id === activeWs.id ? { ...w, ...wsForm } : w));
      toast.success('Updated!');
      setShowWsSettings(false);
    } catch { toast.error('Failed'); }
  }

  async function deleteProject(id, e) {
    e.stopPropagation();
    if (!window.confirm('Delete this project and all its data?')) return;
    try { await api.delete(`/projects/${id}`); toast.success('Deleted'); loadProjects(activeWs.id); }
    catch { toast.error('Failed'); }
  }

  const filtered = projects.filter(p =>
    !searchQ || p.name.toLowerCase().includes(searchQ.toLowerCase())
  );

  const isAdmin = ['admin','member'].includes(user?.role);

  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden', background:'var(--bg)' }}>
      {/* ── Left panel ── */}
      <div style={{ width:220, background:'var(--bg-secondary)', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', flexShrink:0 }}>
        {/* Logo */}
        <div style={{ padding:'14px 14px 10px', borderBottom:'1px solid var(--border)' }}>
          <BrandLogo height={34} style={{ marginBottom:10 }} />

          {/* Workspace picker */}
          {!user?.role === 'client' && workspaces.length > 0 && (
            <div>
              {workspaces.map(ws => (
                <div key={ws.id} style={{ display:'flex', alignItems:'center', gap:7, padding:'6px 8px', borderRadius:7, cursor:'pointer', background: activeWs?.id===ws.id ? 'var(--bg-active)':'transparent', transition:'background .12s', marginBottom:1 }}
                  onClick={() => setActiveWs(ws)}>
                  <div style={{ width:20, height:20, borderRadius:5, background:'linear-gradient(135deg,#5865f2,#34d399)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:9, fontWeight:800, flexShrink:0 }}>{ws.name[0]}</div>
                  <span style={{ flex:1, fontSize:12, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ws.name}</span>
                  {activeWs?.id===ws.id && <ChevronRight size={11} style={{ color:'var(--text-3)' }}/>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Nav links */}
        <div style={{ padding:'8px 8px' }}>
          {[
            { label:'Home',      icon:FolderOpen,  active:true },
            { label:'Workspaces',icon:Layers,       action:() => setShowCreateWs(true) },
            { label:'Teams',     icon:Users,        action:() => navigate('/admin') },
            { label:'Plans',     icon:Zap,          action:() => {} },
          ].map(item => (
            <button key={item.label} style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'7px 10px', borderRadius:7, background: item.active ? 'var(--accent-light)':'transparent', color: item.active ? 'var(--accent)':'var(--text-2)', border:'none', cursor:'pointer', fontFamily:'var(--font)', fontSize:13, fontWeight: item.active ? 500:400, transition:'all .12s', marginBottom:1, textAlign:'left' }}
              onClick={item.action}>
              <item.icon size={14} style={{ flexShrink:0 }}/> {item.label}
            </button>
          ))}
        </div>

        {/* Recent projects */}
        <div style={{ padding:'8px', borderTop:'1px solid var(--border)', marginTop:'auto' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.6px', padding:'4px 8px 6px' }}>Recent Projects</div>
          {projects.slice(0, 5).map(p => (
            <div key={p.id} style={{ display:'flex', alignItems:'center', gap:7, padding:'5px 8px', borderRadius:6, cursor:'pointer', color:'var(--text-2)', fontSize:12, marginBottom:1 }}
              onClick={() => navigate(`/project/${p.id}`)}>
              <Code2 size={12} style={{ color:'var(--accent)', flexShrink:0 }}/>
              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</span>
            </div>
          ))}
        </div>

        {/* User footer */}
        <div style={{ padding:'10px', borderTop:'1px solid var(--border)', display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#5865f2,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:11, flexShrink:0 }}>{user?.name?.[0]?.toUpperCase()}</div>
          <div style={{ flex:1, overflow:'hidden' }}>
            <div style={{ fontSize:12, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize:10, color:'var(--text-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.email}</div>
          </div>
          <ThemeToggle />
          <button className="btn-icon" onClick={logout} title="Sign out"><LogOut size={13}/></button>
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 24px', borderBottom:'1px solid var(--border)', background:'var(--bg)', flexShrink:0 }}>
          <div>
            <h1 style={{ fontSize:20, fontWeight:800, color:'var(--text-1)', marginBottom:2 }}>{activeWs?.name || 'My Workspace'}</h1>
            <div style={{ fontSize:12, color:'var(--text-3)' }}>{members.length} member{members.length!==1?'s':''} · {projects.length} project{projects.length!==1?'s':''}</div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {notifCount > 0 && (
              <div style={{ position:'relative' }}>
                <button className="btn-icon" style={{ position:'relative' }}>
                  <Bell size={16}/>
                  <span style={{ position:'absolute', top:2, right:2, width:7, height:7, borderRadius:'50%', background:'var(--red)', border:'1.5px solid var(--bg)' }}/>
                </button>
              </div>
            )}
            {isAdmin && <>
              <button className="btn btn-secondary btn-sm" onClick={() => { setWsForm({ name:activeWs?.name||'', description:activeWs?.description||'' }); setShowWsSettings(true); }}>
                <Settings size={13}/> Settings
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCreateWs(true)}>
                <Plus size={13}/> Workspace
              </button>
              <button className="btn btn-primary" onClick={() => setShowCreateProject(true)}>
                <Plus size={14}/> New Project
              </button>
            </>}
          </div>
        </div>

        {/* Search + filter */}
        <div style={{ padding:'14px 24px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10, background:'var(--bg)' }}>
          <div style={{ position:'relative', maxWidth:320 }}>
            <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'var(--text-3)' }}/>
            <input style={{ paddingLeft:28, height:32, fontSize:13 }} placeholder={`Search ${projects.length} project${projects.length!==1?'s':''}...`} value={searchQ} onChange={e => setSearchQ(e.target.value)} />
          </div>
          <span style={{ fontSize:12, color:'var(--text-3)', marginLeft:'auto' }}>{filtered.length} project{filtered.length!==1?'s':''}</span>
        </div>

        {/* Content area */}
        <div style={{ flex:1, overflow:'auto', padding:24 }}>
          {filtered.length === 0 && !loading ? (
            <div className="empty-state">
              <div style={{ fontSize:48, marginBottom:4 }}>🚀</div>
              <h3>No projects yet</h3>
              <p>Create your first project to start designing, testing, and documenting APIs.</p>
              {isAdmin && <button className="btn btn-primary" onClick={() => setShowCreateProject(true)}><Plus size={14}/> Create Your First Project</button>}
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:14 }}>
              {filtered.map(p => (
                <div key={p.id} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:12, padding:18, cursor:'pointer', transition:'all .15s', position:'relative' }}
                  onClick={() => navigate(`/project/${p.id}`)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#c7cbf0'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(88,101,242,.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:'linear-gradient(135deg,rgba(88,101,242,.12),rgba(124,58,237,.12))', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Code2 size={18} style={{ color:'var(--accent)' }}/>
                    </div>
                    <div style={{ display:'flex', gap:3 }}>
                      <button style={{ padding:5, background:'none', border:'none', cursor:'pointer', color:'var(--text-3)', borderRadius:5 }}
                        onClick={e => { e.stopPropagation(); navigate(`/project/${p.id}`); }} title="Open">
                        <ExternalLink size={13}/>
                      </button>
                      {isAdmin && <button style={{ padding:5, background:'none', border:'none', cursor:'pointer', color:'var(--text-3)', borderRadius:5 }}
                        onClick={e => deleteProject(p.id, e)} title="Delete">
                        <Trash2 size={13}/>
                      </button>}
                    </div>
                  </div>
                  <div style={{ fontSize:15, fontWeight:700, marginBottom:5, color:'var(--text-1)' }}>{p.name}</div>
                  {p.description && <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:10, lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{p.description}</div>}
                  {p.base_url && <div style={{ fontSize:11, color:'var(--text-3)', background:'var(--bg-secondary)', padding:'3px 8px', borderRadius:4, marginBottom:10, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--mono)' }}>{p.base_url}</div>}
                  <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'var(--text-3)' }}>
                    <Clock size={11}/> {new Date(p.created_at).toLocaleDateString()}
                    {p.assignment_role && <span style={{ marginLeft:'auto', fontSize:10, background:'var(--bg-secondary)', padding:'1px 5px', borderRadius:3 }}>{p.assignment_role}</span>}
                  </div>
                </div>
              ))}

              {/* New project card */}
              {isAdmin && (
                <div style={{ border:'2px dashed var(--border)', borderRadius:12, padding:18, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, color:'var(--text-3)', minHeight:140, transition:'all .15s' }}
                  onClick={() => setShowCreateProject(true)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)'; e.currentTarget.style.background = 'transparent'; }}>
                  <Plus size={24} style={{ opacity:.5 }}/>
                  <span style={{ fontSize:13, fontWeight:500 }}>New Project</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Right panel: members ── */}
      <div style={{ width:220, borderLeft:'1px solid var(--border)', background:'var(--bg-secondary)', overflow:'auto', flexShrink:0, display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'14px 14px 8px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--text-2)', display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
            <Users size={13}/> Team Members
          </div>
          {isAdmin && (
            <button className="btn btn-sm btn-secondary" style={{ width:'100%', justifyContent:'center' }} onClick={() => navigate('/admin')}>
              <Plus size={12}/> Invite Member
            </button>
          )}
        </div>
        <div style={{ padding:'8px' }}>
          {members.map(m => (
            <div key={m.id} style={{ display:'flex', alignItems:'center', gap:7, padding:'6px 8px', borderRadius:7, marginBottom:2 }}>
              <div style={{ width:26, height:26, borderRadius:'50%', background:'linear-gradient(135deg,#5865f2,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:10, flexShrink:0 }}>{m.name[0]?.toUpperCase()}</div>
              <div style={{ flex:1, overflow:'hidden' }}>
                <div style={{ fontSize:12, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.name}</div>
                <span style={{ fontSize:9, background:'var(--bg-active)', padding:'1px 5px', borderRadius:3, color:'var(--text-2)', fontWeight:600, textTransform:'uppercase' }}>{m.role}</span>
              </div>
            </div>
          ))}
          {!members.length && <div style={{ fontSize:12, color:'var(--text-3)', padding:'8px', textAlign:'center' }}>No members yet</div>}
        </div>

        {/* Workspaces list */}
        <div style={{ padding:'8px', borderTop:'1px solid var(--border)', marginTop:'auto' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.6px', padding:'4px 8px 6px' }}>Workspaces</div>
          {workspaces.map(ws => (
            <div key={ws.id} style={{ display:'flex', alignItems:'center', gap:7, padding:'6px 8px', borderRadius:6, cursor:'pointer', marginBottom:1, background: activeWs?.id===ws.id ? 'var(--accent-light)':'' }}
              onClick={() => setActiveWs(ws)}>
              <div style={{ width:20, height:20, borderRadius:5, background:'linear-gradient(135deg,#5865f2,#34d399)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:9, fontWeight:800, flexShrink:0 }}>{ws.name[0]}</div>
              <span style={{ fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color: activeWs?.id===ws.id ? 'var(--accent)':'var(--text-2)' }}>{ws.name}</span>
              {ws.my_role==='admin' && <span style={{ fontSize:9, color:'var(--red)', background:'#fef2f2', padding:'1px 4px', borderRadius:3, marginLeft:'auto' }}>admin</span>}
            </div>
          ))}
          {isAdmin && (
            <button style={{ display:'flex', alignItems:'center', gap:6, width:'100%', padding:'6px 8px', borderRadius:6, background:'none', border:'none', color:'var(--accent)', fontSize:12, cursor:'pointer', fontFamily:'var(--font)', marginTop:4 }}
              onClick={() => setShowCreateWs(true)}>
              <Plus size={12}/> New Workspace
            </button>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {showCreateProject && (
        <div className="modal-overlay" onClick={() => setShowCreateProject(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">New Project</div>
              <button className="btn-icon" onClick={() => setShowCreateProject(false)}><X size={16}/></button>
            </div>
            <form onSubmit={createProject}>
              <div className="form-group"><label className="form-label">Project Name *</label>
                <input placeholder="e.g. Pet Store API" value={projectForm.name} onChange={e=>setProjectForm(f=>({...f,name:e.target.value}))} required autoFocus /></div>
              <div className="form-group"><label className="form-label">Description</label>
                <textarea rows={2} placeholder="What does this API do?" value={projectForm.description} onChange={e=>setProjectForm(f=>({...f,description:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Base URL</label>
                <input placeholder="https://api.example.com" className="mono" value={projectForm.base_url} onChange={e=>setProjectForm(f=>({...f,base_url:e.target.value}))} />
                <div className="form-hint">Used as the base for all API requests. Supports {'{{ENV_VAR}}'} substitution.</div></div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowCreateProject(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Creating…':'Create Project'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateWs && (
        <div className="modal-overlay" onClick={()=>setShowCreateWs(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">New Workspace</div><button className="btn-icon" onClick={()=>setShowCreateWs(false)}><X size={16}/></button></div>
            <form onSubmit={createWorkspace}>
              <div className="form-group"><label className="form-label">Workspace Name *</label>
                <input placeholder="e.g. Acme Corp" value={wsForm.name} onChange={e=>setWsForm(f=>({...f,name:e.target.value}))} required autoFocus /></div>
              <div className="form-group"><label className="form-label">Description</label>
                <input placeholder="Optional" value={wsForm.description} onChange={e=>setWsForm(f=>({...f,description:e.target.value}))} /></div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowCreateWs(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Creating…':'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWsSettings && (
        <div className="modal-overlay" onClick={()=>setShowWsSettings(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Workspace Settings</div><button className="btn-icon" onClick={()=>setShowWsSettings(false)}><X size={16}/></button></div>
            <form onSubmit={updateWorkspace}>
              <div className="form-group"><label className="form-label">Name</label>
                <input value={wsForm.name} onChange={e=>setWsForm(f=>({...f,name:e.target.value}))} required /></div>
              <div className="form-group"><label className="form-label">Description</label>
                <textarea rows={2} value={wsForm.description} onChange={e=>setWsForm(f=>({...f,description:e.target.value}))} /></div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowWsSettings(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Save size={13}/> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
